import React, { useState } from "react";
import { Button } from "antd";
import { ImportEmployeeAPI } from "../Api";
import { UploadTemplate } from "../../common/components/UploadTemplate/";
import { useSettingValue } from "../../utils/Hooks";
import { Trans } from "../../common/components/MockedTrans";

const useTranslation = () => {
  return { t: (text) => text };
};

export const ImportCreateEmployeeModal = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isUsingCustomKey = useSettingValue(
    "T006_ALLOW_FIELD_SELECTION_AS_EMPLOYEE_KEY",
  );
  const enablePreferredChannel = useSettingValue(
    "T015_ENABLE_PREFERRED_CHANNEL",
  );
  const customKey = useSettingValue("EMPLOYEE_CUSTOM_KEY");

  const { t } = useTranslation();

  const fields = [
    {
      name: "first_name",
      description: t("Nome do(a) funcionário(a)."),
      required: true,
    },
    {
      name: "last_name",
      description: t("Sobrenome do(a) funcionário(a)."),
      required: true,
    },
    {
      name: "email",
      description: t("E-mail do(a) funcionário(a)."),
      required: true,
    },
    enablePreferredChannel && {
      name: "phone_number",
      description: t("Telefone celular do(a) funcionário(a)."),
      required: false,
    },
    enablePreferredChannel && {
      name: "preferred_channel",
      description: t(
        "Canal utilizado para criação e redefinição de senha. Defina como “phone_number” (telefone celular) ou “email” (e-mail). Quando o(a) funcionário(a) não tiver e-mail corporativo, priorize o telefone celular como opção.",
      ),
      required: true,
    },
    {
      name: "username",
      description: t("Nome de usuário do(a) funcionário(a)"),
      required: true,
    },
    (!isUsingCustomKey || customKey === "employee_code") && {
      name: "employee_code",
      description: t(
        "Código do(a) funcionário(a), pode ser qualquer texto, número ou caractere especial que identifique o(a) funcionário(a), sendo necessário um código único por funcionário(a).",
      ),
      required: true,
    },
    {
      name: "start_date",
      description: t(
        "Data de contratação do(a) funcionário(a). Campo obrigatório, deve ser uma data no formato 'YYYY-MM-DD'.",
      ),
      required: true,
    },
    {
      name: "gender",
      description: t(
        "Gênero do(a) funcionário(a). Campo opcional, deve ser uma das seguintes opções: 'male' para masculino, 'female' para feminino ou 'unknown' para não disponível.",
      ),
      required: false,
    },
    {
      name: "cpf",
      description: `${t("CPF do(a) funcionário(a).")} ${
        customKey === "cpf"
          ? ""
          : t(" Campo opcional, deve conter um número de CPF válido.")
      }`,
      required: isUsingCustomKey && customKey === "cpf",
    },
    {
      name: "birth_date",
      description: t(
        "Data de nascimento do(a) funcionário(a). Campo opcional, deve ser uma data no formato 'YYYY-MM-DD'.",
      ),
      required: false,
    },
    {
      name: "company_referal",
      description: t(
        "Como o(a) funcionário(a) conheceu a empresa. Campo opcional, é um campo de texto livre.",
      ),
      required: false,
    },
    {
      name: "work_type",
      description: t(
        "Tipo de trabalho do(a) funcionário(a). Campo opcional, deve ser uma das seguintes opções: hybrid (Híbrido), inoffice (Presencial), remote (Remoto) ou unknown (Não disponível).",
      ),
      required: false,
    },
    {
      name: "work_city",
      description: t(
        "Local de trabalho do(a) funcionário(a). Campo opcional, deve ser o nome da cidade, estado ou país onde o(a) funcionário(a) trabalha.",
      ),
      required: false,
    },
    {
      name: "manager",
      description: t("Código do gestor do(a) funcionário(a). Campo opcional."),
      required: false,
    },
    {
      name: "area",
      description: t("Código da área do(a) funcionário(a). Campo opcional."),
      required: false,
    },
    {
      name: "position",
      description: t("Código do cargo do(a) funcionário(a). Campo opcional."),
      required: false,
    },
    {
      name: "corporation",
      description: t(
        "Código da empresa a qual o(a) funcionário(a) pertence. Campo opcional.",
      ),
      required: false,
    },
    {
      name: "branch_corporation",
      description: t(
        "Código da filial a qual o(a) funcionário(a) pertence. Campo opcional.",
      ),
      required: false,
    },
    {
      name: "systems_permissions",
      description: t(
        "Sistemas nos quais o(a) funcionário(a) está ativo(a). Campo opcional — deve ser uma lista contendo os códigos dos sistemas aos quais esse(a) funcionário(a) tem acesso separados por vírgula e sem espaço. As opções disponíveis são:",
      ),
      required: false,
      options: [
        { value: "full", label: t("concede acesso ao módulo People Hub;") },
        {
          value: "organograma",
          label: t("concede acesso ao módulo de Gestão de Organograma;"),
        },
        {
          value: "metas",
          label: t("concede acesso ao módulo de Metas & PDI;"),
        },
        {
          value: "performance",
          label: t("concede acesso ao módulo de Desempenho."),
        },
      ],
    },
  ].filter(Boolean);

  const uploadCreateEmployeeRequest = (table) => {
    ImportEmployeeAPI.create_import(
      isUsingCustomKey && customKey !== "employee_code"
        ? table.map((obj) => ({ ...obj, employee_code: obj[customKey] }))
        : table,
    );
  };

  return (
    <>
      <Button type="text" onClick={() => setIsModalOpen(true)}>
        Criar Funcionários
      </Button>
      <UploadTemplate
        open={isModalOpen}
        action={"create_employee"}
        onClose={() => setIsModalOpen(false)}
        modalTitle={"Importar Funcionários para Criação"}
        fields={fields}
        uploadRequest={uploadCreateEmployeeRequest}
      />
    </>
  );
};
